'use client';

import { useMemo, useRef, useState } from 'react';
import { consumeToolUsage } from '@/lib/browser-tool-usage';

type SheetSize = '4x8' | '4x12';

const SHEET_AREA_SQFT: Record<SheetSize, number> = { '4x8': 32, '4x12': 48 };

export default function BrowserDrywallCalculator() {
  const fired = useRef(false);
  const fireUsage = () => {
    if (fired.current) return;
    fired.current = true;
    void consumeToolUsage('drywall-calculator', '').catch(() => undefined);
  };

  const [lengthFt, setLengthFt] = useState(12);
  const [widthFt, setWidthFt] = useState(10);
  const [heightFt, setHeightFt] = useState(8);
  const [includeCeiling, setIncludeCeiling] = useState(true);
  const [sheetSize, setSheetSize] = useState<SheetSize>('4x8');
  const [wastePercent, setWastePercent] = useState(10);

  function onChange<T>(setter: (v: T) => void) {
    return (v: T) => {
      fireUsage();
      setter(v);
    };
  }

  const result = useMemo(() => {
    const perimeter = 2 * (lengthFt + widthFt);
    const wallArea = perimeter * heightFt;
    const ceilingArea = includeCeiling ? lengthFt * widthFt : 0;
    const rawArea = wallArea + ceilingArea;
    const areaWithWaste = rawArea * (1 + wastePercent / 100);
    const sheets = Math.ceil(areaWithWaste / SHEET_AREA_SQFT[sheetSize]);
    return { rawArea, areaWithWaste, sheets };
  }, [lengthFt, widthFt, heightFt, includeCeiling, sheetSize, wastePercent]);

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Drywall</span>
        <h2 className="tool-live-title">Drywall calculator</h2>
        <p className="tool-live-subtitle">
          Enter a room&apos;s dimensions to get the wall (and ceiling) area and how many drywall
          sheets you need.
        </p>
      </div>

      <div className="tool-live-grid">
        <section
          className="tool-live-uploader"
          style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))' }}
        >
          <label className="tool-live-field">
            <span>Room length (ft)</span>
            <input
              min={0}
              onChange={(e) => onChange(setLengthFt)(Number(e.target.value) || 0)}
              step={0.5}
              type="number"
              value={lengthFt}
            />
          </label>
          <label className="tool-live-field">
            <span>Room width (ft)</span>
            <input
              min={0}
              onChange={(e) => onChange(setWidthFt)(Number(e.target.value) || 0)}
              step={0.5}
              type="number"
              value={widthFt}
            />
          </label>
          <label className="tool-live-field">
            <span>Wall height (ft)</span>
            <input
              min={0}
              onChange={(e) => onChange(setHeightFt)(Number(e.target.value) || 0)}
              step={0.5}
              type="number"
              value={heightFt}
            />
          </label>
          <label className="tool-live-field">
            <span>Sheet size</span>
            <select
              onChange={(e) => onChange(setSheetSize)(e.target.value as SheetSize)}
              value={sheetSize}
            >
              <option value="4x8">4 x 8 ft (32 sq ft)</option>
              <option value="4x12">4 x 12 ft (48 sq ft)</option>
            </select>
          </label>
          <label className="tool-live-field">
            <span>Waste allowance (%)</span>
            <input
              min={0}
              onChange={(e) => onChange(setWastePercent)(Number(e.target.value) || 0)}
              type="number"
              value={wastePercent}
            />
          </label>
          <label className="tool-live-checkbox">
            <input
              checked={includeCeiling}
              onChange={(e) => onChange(setIncludeCeiling)(e.target.checked)}
              type="checkbox"
            />
            <span>Include the ceiling</span>
          </label>
        </section>

        <section className="tool-live-preview">
          <p className="tool-live-panel-note">
            <strong>{result.rawArea.toFixed(0)} sq ft</strong>
            <span>total drywall area before waste</span>
          </p>
          <p className="tool-live-panel-note">
            <strong>{result.sheets} sheets</strong>
            <span>
              of {sheetSize.replace('x', ' x ')} ft board, including a {wastePercent}% cutting
              allowance
            </span>
          </p>
          <p className="tool-live-panel-note">
            Openings (doors, windows) aren&apos;t subtracted here since cut-offs from around them
            usually still count toward waste; subtract manually if a room has unusually large
            openings.
          </p>
        </section>
      </div>
    </div>
  );
}
